/* ---------------------------------------------------------------- button -- */
.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: var(--touch);
  padding-inline: var(--space-4);
  border-radius: var(--radius-card);
  font-weight: 600;
  letter-spacing: -0.005em;
  transition:
    background-color var(--duration-micro) var(--ease),
    box-shadow var(--duration-micro) var(--ease),
    opacity var(--duration-micro) var(--ease);
}

.button:disabled {
  opacity: 0.5;
  pointer-events: none;
}

/*
 * Madder is the CTA colour and nothing else in the app may use it.
 *
 * The lit top edge and the coloured contact shadow are the same two ideas as
 * `.paper`, mixed from the accent instead of the ink: the one button that
 * matters should look pressed *into* the page rather than printed on it.
 */
.primary {
  background: var(--accent);
  color: var(--accent-on);
  min-height: var(--touch-cta);
  width: 100%;
  box-shadow:
    inset 0 1px 0 var(--edge-media),
    0 2px 10px -3px color-mix(in srgb, var(--accent) 55%, transparent);
}

.primary:active {
  box-shadow:
    inset 0 1px 0 var(--edge-media),
    0 1px 4px -2px color-mix(in srgb, var(--accent) 45%, transparent);
}

/* Surface, border and lift come from the global `.paper` class. */
.secondary {
  color: var(--ink);
}

.ghost {
  background: transparent;
  color: var(--ink-muted);
}

.destructive {
  background: var(--tint-destructive);
  color: var(--destructive);
}

/* ------------------------------------------------------------ icon button -- */
.iconButton {
  display: inline-grid;
  place-items: center;
  inline-size: var(--touch);
  block-size: var(--touch);
  border-radius: var(--radius-pill);
  color: var(--ink);
  transition: background-color var(--duration-micro) var(--ease);
}

.iconButton:active {
  background: var(--tint-ink);
}

/* ------------------------------------------------------------------ chip -- */
.chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  min-height: 32px;
  padding-inline: var(--space-3);
  border-radius: var(--radius-pill);
  color: var(--ink-muted);
  font-size: var(--text-caption);
  font-weight: 500;
  white-space: nowrap;
  transition: color var(--duration-micro) var(--ease);
}

/* Transparent on purpose: the travelling pill sits at z-index -1 and a painted
   background here would hide it. */
.chipSelected {
  background: transparent;
  border: 1px solid transparent;
  color: var(--ink);
  font-weight: 600;
}

.chipSelectedFlat {
  background: var(--tint-accent-strong);
}

/* The moving pill behind the selected chip; siblings share one layoutId. */
.chipPill {
  position: absolute;
  inset: 0;
  border-radius: var(--radius-pill);
  background: var(--tint-accent-strong);
  z-index: -1;
}

.chipStatic {
  pointer-events: none;
}

.chipRow {
  display: flex;
  gap: var(--space-2);
  overflow-x: auto;
  scrollbar-width: none;
  padding-inline: var(--space-4);
  padding-block: var(--space-2);
  scroll-padding-inline: var(--space-4);
}

.chipRow::-webkit-scrollbar {
  display: none;
}

/* --------------------------------------------------------------- badge -- */
.badge {
  display: inline-grid;
  place-items: center;
  min-inline-size: 18px;
  block-size: 18px;
  padding-inline: 5px;
  border-radius: var(--radius-pill);
  background: var(--accent);
  color: var(--accent-on);
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  /* Reads as a badge sitting on the control rather than a dot cut out of it. */
  box-shadow: 0 0 0 2px var(--bg);
}

/* -------------------------------------------------------------- skeleton -- */
.skeleton {
  position: relative;
  overflow: hidden;
  background: var(--shimmer);
  border-radius: var(--radius-card);
}

.skeleton::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, var(--shimmer-hi), transparent);
  animation: shimmer 1.4s var(--ease) infinite;
}

/* ------------------------------------------------------------- messages -- */
.state {
  display: grid;
  justify-items: center;
  align-content: center;
  gap: var(--space-3);
  min-block-size: 55dvh;
  padding: var(--space-6) var(--space-4);
  text-align: center;
}

/* A paper disc on the linen wall, not a grey blob: an empty state should look
   like part of the ledger rather than a missing asset. The outer ring is the
   shadow the disc would cast if it were a mount. */
/* Stock, hairline and lift come from the global `.paper` class; the ring is
   the shadow the mount casts, and is all this rule adds. */
.stateMark {
  display: grid;
  place-items: center;
  inline-size: 76px;
  block-size: 76px;
  border-radius: var(--radius-pill);
  color: var(--ink-muted);
  box-shadow:
    var(--lift),
    0 0 0 8px color-mix(in srgb, var(--ink) 3%, transparent);
}

.stateTitle {
  font-size: 1.125rem;
  font-weight: 600;
  letter-spacing: -0.005em;
}

.stateBody {
  color: var(--ink-muted);
  max-inline-size: 30ch;
  line-height: 1.55;
}

.stateAction {
  margin-block-start: var(--space-2);
}

/* -------------------------------------------------------------- progress -- */
.progress {
  block-size: 3px;
  border-radius: var(--radius-pill);
  background: var(--tint-ink);
  overflow: hidden;
}

/*
 * Full width, scaled down: a transform stays on the compositor where an
 * animated width relayouts the bar every frame. The origin is the only
 * physical value here, and it is pinned per direction just below so the bar
 * still grows from the leading edge in both.
 */
.progressFill {
  inline-size: 100%;
  block-size: 100%;
  border-radius: var(--radius-pill);
  background: var(--accent);
  transform-origin: right;
}

[dir="ltr"] .progressFill {
  transform-origin: left;
}

/* -------------------------------------------------------------- spinner -- */
.spinner {
  display: inline-grid;
  place-items: center;
  color: var(--ink-muted);
  animation: spin 900ms linear infinite;
}

/* --------------------------------------------------------------- divider -- */
.divider {
  block-size: 1px;
  background: var(--border);
  border: 0;
}

/* ------------------------------------------------------- language toggle -- */
/*
 * The same control as `.segmented` in fields.module.css, at chrome size: a
 * sunken track with one lifted tile sliding between the options. Both read the
 * same tokens so the two never drift apart.
 */
.langToggle {
  position: relative;
  display: inline-grid;
  grid-auto-flow: column;
  gap: 2px;
  padding: 3px;
  border-radius: var(--radius-pill);
  background: var(--tint-ink);
  box-shadow: var(--well);
}

.langOption {
  position: relative;
  z-index: 1;
  min-inline-size: 40px;
  min-block-size: 38px;
  padding-inline: var(--space-2);
  border-radius: var(--radius-pill);
  font-size: var(--text-caption);
  font-weight: 600;
  color: var(--ink-muted);
  transition: color var(--duration-micro) var(--ease);
}

.langOptionActive {
  color: var(--ink);
}

.langPill {
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: var(--radius-pill);
  background: var(--elevated);
  box-shadow: var(--lift);
}
